import { useCallback, useState } from "react";
import { ConditionBuilder } from "./ConditionBuilder";
import { ConfirmDialog } from "./ConfirmDialog";
import type { Condition, EditorMetadata } from "./types";
import type { EditorMutations } from "./useEditorMutations";

/**
 * A condition slot — on a transition or on a rule.
 *
 * The same component serves both because the engine treats them the same way: a condition
 * either matches an event or it does not, and the tree is identical. Only what holds it differs.
 */
export function ConditionSection({
    condition,
    attachTo,
    metadata,
    mutations,
    dirtyKey,
    onDirtyChange,
    /** What "no condition" means here, which differs between a transition and a rule. */
    absentMeaning,
}: {
    condition: Condition | null;
    attachTo: { kind: "transition"; id: string } | { kind: "rule"; id: string };
    metadata: EditorMetadata;
    mutations: EditorMutations;
    dirtyKey: string;
    onDirtyChange: (key: string, dirty: boolean) => void;
    absentMeaning: string;
}) {
    const [pendingRemoval, setPendingRemoval] = useState(false);

    // Stable identity. An inline arrow here is what fed the render loop the builder now guards
    // against; keeping it stable closes the same hole one level up rather than relying only on
    // the guard.
    const reportDirty = useCallback(
        (dirty: boolean) => onDirtyChange(dirtyKey, dirty),
        [onDirtyChange, dirtyKey],
    );

    const save = mutations.saveConditionTree;
    const isSavingThis =
        save.isLoading && save.variables?.conditionId === condition?.id;
    const errorForThis =
        save.variables?.conditionId === condition?.id ? save.error : null;

    if (!condition) {
        return (
            <>
                <p className="aer-wf-panel__empty">{absentMeaning}</p>
                <button
                    type="button"
                    className="aer-wf-button aer-wf-button--secondary"
                    disabled={mutations.addCondition.isLoading}
                    onClick={() =>
                        mutations.addCondition.mutate({
                            name:
                                attachTo.kind === "transition"
                                    ? "Transition condition"
                                    : "Rule condition",
                            attachTo,
                        })
                    }
                >
                    {mutations.addCondition.isLoading
                        ? "Adding…"
                        : "Add a condition"}
                </button>
            </>
        );
    }

    return (
        <>
            <div className="aer-wf-condition__header">
                <span className="aer-wf-condition__name">{condition.name}</span>
                {!condition.isUsable && (
                    <span className="aer-wf-badge aer-wf-badge--danger">
                        cannot fire
                    </span>
                )}
                <button
                    type="button"
                    className="aer-wf-button aer-wf-button--secondary"
                    onClick={() => setPendingRemoval(true)}
                >
                    Remove condition
                </button>
            </div>

            {condition.problems.length > 0 && (
                <div className="aer-wf-notice aer-wf-notice--danger">
                    <ul>
                        {condition.problems.map((problem) => (
                            <li key={problem}>{problem}</li>
                        ))}
                    </ul>
                </div>
            )}

            <ConditionBuilder
                condition={condition}
                metadata={metadata}
                isSaving={isSavingThis}
                saveError={errorForThis}
                onDirtyChange={reportDirty}
                onSave={(input) =>
                    save.mutate({ conditionId: condition.id, input })
                }
            />

            {pendingRemoval && (
                <ConfirmDialog
                    title={`Remove the condition "${condition.name}"?`}
                    consequences={["the condition and its groups"]}
                    note={
                        attachTo.kind === "rule"
                            ? "The rule survives and becomes unconditional, so it will match every event — and shadow any rule below it."
                            : "The transition survives and will fire whenever it is reached."
                    }
                    confirmLabel="Remove condition"
                    isPending={mutations.detachCondition.isLoading}
                    onConfirm={() =>
                        mutations.detachCondition.mutate(condition.id, {
                            onSuccess: () => setPendingRemoval(false),
                        })
                    }
                    onCancel={() => setPendingRemoval(false)}
                />
            )}
        </>
    );
}
